import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isEmpty, isEqual } from 'lodash';

import Link from 'buyside/components/presentation/Link/link';
import LoadingSpinner from 'buyside/components/presentation/LoadingSpinner/loadingSpinner';
import SavedSearchButton from 'buyside/components/presentation/SavedSearch/SavedSearchButton/savedSearchButton';
import SavedSearchListItem from 'buyside/components/presentation/SavedSearch/SavedSearchList/savedSearchListItem';

import { manageSavedSearchesUrl } from 'buyside/util/urlHelper';
import { sanitizeParams } from 'buyside/util/filterRedirect';

import {
  FundProfileIndexSavedSearchQuery,
  IdeaIndexSavedSearchQuery
} from 'interfaces/graphql-typings';

import styles from './savedSearchStyles.scss';

const SavedSearch: React.FC<{
  loading: boolean;
  searchType: 'Idea' | 'FundProfile';
  savedSearchList:
    | FundProfileIndexSavedSearchQuery['fundProfilesSavedSearchList']
    | IdeaIndexSavedSearchQuery['ideasSavedSearchList']
    | undefined;
}> = ({ loading, savedSearchList, searchType }) => {
  const [searchParams] = useSearchParams();
  const [activeSavedSearch, setActiveSavedSearch] = useState('');

  useEffect(() => {
    if (savedSearchList && !isEmpty(savedSearchList.edges)) {
      const mappedSearches = savedSearchList?.edges?.map(edge => ({
        id: edge?.node?.id,
        criteria: sanitizeParams(JSON.parse(edge?.node?.savedSearch?.criteria))
      }));

      const query: { [key: string]: string | string[] } = {};
      for (const paramName of searchParams.keys()) {
        const paramValue = searchParams.getAll(paramName);
        query[paramName] = paramValue.length > 1 ? paramValue : paramValue[0];
      }

      let foundMatch = false;
      mappedSearches?.forEach(mappedSearch => {
        if (!foundMatch && isEqual(mappedSearch.criteria, query)) {
          foundMatch = true;
          setActiveSavedSearch(mappedSearch?.id || '');
        }
      });
      if (!foundMatch && activeSavedSearch) setActiveSavedSearch('');
    }
    if (!searchParams.toString()) {
      if (activeSavedSearch) setActiveSavedSearch('');
    }
  }, [savedSearchList, activeSavedSearch, searchParams]);

  const savedSearchNames = savedSearchList?.edges?.map(savedSearch =>
    savedSearch?.node?.name ? savedSearch.node.name.toLowerCase() : ''
  );

  return (
    <>
      {(loading && <LoadingSpinner type="secondary" />) ||
        (!activeSavedSearch && (
          <SavedSearchButton savedSearchNames={savedSearchNames || []} searchType={searchType} />
        ))}
      {Boolean(savedSearchList?.edges?.length) &&
        savedSearchList?.edges?.map(item => {
          if (!item || !item.node) return null;

          return (
            <SavedSearchListItem
              key={item.node.id}
              activeSavedSearch={activeSavedSearch}
              openSavedSearch={(savedSearchId: string): void => setActiveSavedSearch(savedSearchId)}
              clearSavedSearches={(): void => setActiveSavedSearch('')}
              savedSearch={item.node}
            />
          );
        })}
      {!loading &&
        ((savedSearchList && savedSearchList?.edges?.length && (
          <nav className={styles.savedSearchLinkArea}>
            <Link to={manageSavedSearchesUrl()}>Manage Saved Searches</Link>
          </nav>
        )) || (
          <div className={styles.savedSearchText}>
            <em>
              This is where your saved searches will appear. Once you save a search, you can use it
              to easily filter research results.
            </em>
          </div>
        ))}
    </>
  );
};

export default SavedSearch;
